import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CustomerHeader from '../../components/ui/CustomerHeader';
import AuthenticationModal from '../../components/ui/AuthenticationModal';
import CartSidebar from '../../components/ui/CartSidebar';
import AccountSidebar from './components/AccountSidebar';
import ProfileSettings from './components/ProfileSettings';
import OrderHistory from './components/OrderHistory';
import AddressBook from './components/AddressBook';
import PaymentMethods from './components/PaymentMethods';
import Preferences from './components/Preferences';
import SecuritySettings from './components/SecuritySettings';
import QuickActionCards from './components/QuickActionCards';
import Footer from '../../components/ui/Footer';

const UserAccountDashboard = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(3);
  const [activeSection, setActiveSection] = useState(location?.state?.section || 'profile');
  useEffect(() => {
    if (location?.state?.section) setActiveSection(location.state.section);
  }, [location?.state?.section]);
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phone: '',
    profileImage: '',
    profileImageAlt: '',
    joinDate: '',
    // Stats zeroed until orders are fetched from a real source.
    totalOrders: 0,
    totalSpent: 0,
    loyaltyPoints: 0,
  });

  // Update userData with real user data from AuthContext when available
  useEffect(() => {
    if (user?.email) {
      setUserData(prev => ({
        ...prev,
        email: user?.email,
        name: user?.name || prev?.name,
        role: user?.role,
        joinDate: user?.joinDate || prev?.joinDate || '',
      }));
    }
  }, [user]);

  // Show loading while authentication is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading your account...</p>
        </div>
      </div>
    );
  }

  // Handle navigation from mobile menu
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setShowMobileSidebar(false);
  };

  const handleAuthentication = (authData) => {
    console.log('User authenticated:', authData);
    setShowAuthModal(false);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSettings userData={userData} onUpdateUser={setUserData} />;
      case 'orders':
        return <OrderHistory />;
      case 'addresses':
        return <AddressBook />;
      case 'payments':
        return <PaymentMethods />;
      case 'preferences':
        return <Preferences />;
      case 'security':
        return <SecuritySettings />;
      default:
        return <ProfileSettings userData={userData} onUpdateUser={setUserData} />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader
        cartItemCount={0}
        onCartClick={() => setShowCartSidebar(true)}
      />

      <div className="pt-16 flex-1 overflow-x-hidden">
        <div className="w-full max-w-full">
          <div className="flex flex-col lg:flex-row lg:min-h-[calc(100vh-4rem)]">
            {/* Account Sidebar */}
            <AccountSidebar
              isOpen={showMobileSidebar}
              onClose={() => setShowMobileSidebar(false)}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              userData={userData}
            />

            {/* Main Content Area */}
            <div className="flex-1 min-w-0 w-full lg:w-auto overflow-x-hidden">
              {/* Page Header */}
              <div className="bg-white border-b border-border px-4 sm:px-6 py-4 lg:px-8">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h1 className="text-xl sm:text-2xl font-heading font-semibold text-foreground truncate">
                      My Account
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      Welcome back, {userData?.name || user?.email}
                    </p>
                  </div>

                  {/* Mobile Menu Button */}
                  <button
                    onClick={() => setShowMobileSidebar(true)}
                    className="lg:hidden p-2 rounded-lg border border-input hover:bg-accent hover:text-accent-foreground transition-colors flex-shrink-0"
                    aria-label="Open account menu"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Quick Action Cards - Show only on profile section */}
              {activeSection === 'profile' && (
                <div className="px-4 sm:px-6 py-6 lg:px-8">
                  <QuickActionCards
                    userData={userData}
                    onSectionChange={setActiveSection}
                  />
                </div>
              )}

              {/* Active Section Content */}
              <div className="px-4 sm:px-6 pb-8 lg:px-8">
                {renderActiveSection()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Modals and Sidebars */}
      <AuthenticationModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthenticate={handleAuthentication}
        initialMode="login"
      />

      <CartSidebar
        isOpen={showCartSidebar}
        onClose={() => setShowCartSidebar(false)}
        cartItems={[]}
        onUpdateQuantity={() => {}}
        onRemoveItem={() => {}}
        onCheckout={() => {}}
      />
    </div>
  );
};

export default UserAccountDashboard;